import isEqual from 'lodash/isEqual';
import { PaginatorStore, TableManagerStore } from '@warehouse/shared/ui';
import { deepClone, PersistQueryParamsConfig } from '@warehouse/shared/util';
import { GridLogicOperator, NxObjectType, SearchMode } from '@warehouse/object-browser/core';
import { objectBrowserTableColumns } from '../feature-object-browser/columns';
import { WildCardSearchStore } from '../feature-wild-card-search/domain';
import {
	LicensorColumnName,
	NameColumnName,
	TitleColumnName,
	TitleIdsColumnName,
} from '../feature-object-browser/specific-column-names';

type PaginatorConfigOptions = {
	initialValue?: number;
	sessionStorage?: string;
	localStorage?: string;
};

export function paginatorStoreConfig({
	initialValue,
	sessionStorage,
	localStorage,
}: PaginatorConfigOptions = {}): PersistQueryParamsConfig<PaginatorStore> {
	return {
		page: {
			type: 'number',
			defaultValue: 1,
		},
		perPage: {
			type: 'number',
			defaultValue: initialValue,
			...(sessionStorage && { sessionStorageKeyPrefix: sessionStorage }),
			...(localStorage && { localStorageKey: localStorage }),
		},
	};
}

export function wildCardSearchStoreConfig(sessionStorage: string): PersistQueryParamsConfig<WildCardSearchStore> {
	return {
		searchQuery: {
			type: 'string',
			defaultValue: '',
			sessionStorageKeyPrefix: sessionStorage,
		},
		searchMode: {
			type: 'string',
			defaultValue: SearchMode.THIS_FOLDER,
			sessionStorageKeyPrefix: sessionStorage,
		},
	};
}

export function tableManagerStoreConfig(sessionStorage: string): PersistQueryParamsConfig<TableManagerStore> {
	return {
		sort: {
			type: 'array',
			defaultValue: [{ field: 'name', sort: 'asc' }],
			sessionStorageKeyPrefix: sessionStorage,
		},
		visibility: {
			type: 'object',
			keyName: 'column-visibility',
			defaultValue: {
				[NameColumnName]: true,
				[LicensorColumnName]: true,
				[TitleColumnName]: true,
				bucket: false,
				fileSize: true,
				itemType: true,
				storageTier: true,
				lastModifiedAt: true,
				eTag: false,
				online: false,
				key: false,
				s3Uri: false,
				uuid: false,
				uploadedByOrganization: false,
				uploadedByUser: false,
				[TitleIdsColumnName]: false,
			},
			sessionStorageKeyPrefix: sessionStorage,
		},
		pinned: {
			type: 'object',
			keyName: 'pinned-columns',
			defaultValue: {
				left: ['itemType', 'name'],
			},
			sessionStorageKeyPrefix: sessionStorage,
		},
		order: {
			type: 'array',
			keyName: 'column-order',
			defaultValue: objectBrowserTableColumns.map((c) => c.field),
			sessionStorageKeyPrefix: sessionStorage,
		},
		filter: {
			type: 'object',
			keyName: 'filters',
			defaultValue: {
				items: [{ field: 'online', operator: 'is', value: true }],
				logicOperator: GridLogicOperator.AND,
			},
			sessionStorageKeyPrefix: sessionStorage,
			customSerializer: {
				isDefault(value, defaultValue) {
					const cleanedValue = deepClone(value);
					cleanedValue.items.forEach((_, idx) => {
						// this is a non-typed key from muix internal type
						delete (cleanedValue.items[idx] as any).fromInput;
						delete cleanedValue.items[idx].id;
					});
					return isEqual(cleanedValue, defaultValue);
				},
			},
		},
	};
}

export function titlesObjectTabObjectBrowserStoreConfig(
	sessionStorage: string,
): PersistQueryParamsConfig<TableManagerStore> {
	return {
		...tableManagerStoreConfig(sessionStorage),
		filter: {
			...tableManagerStoreConfig(sessionStorage).filter,
			defaultValue: {
				items: [
					{ id: 1, field: 'online', operator: 'is', value: true },
					{ id: 2, field: 'itemType', operator: 'is', value: NxObjectType.File },
				],
				logicOperator: GridLogicOperator.AND,
			},
		},
		visibility: {
			...tableManagerStoreConfig(sessionStorage).visibility,
			defaultValue: {
				...tableManagerStoreConfig(sessionStorage).visibility?.defaultValue,
				[LicensorColumnName]: false,
				[TitleColumnName]: false,
				key: true,
			},
		},
		sort: {
			type: 'array',
			defaultValue: [{ field: 'name', sort: 'asc' }],
			sessionStorageKeyPrefix: sessionStorage,
		},
	};
}
